'use client';

import React from 'react';
import { useState, useRef } from 'react';
import clientImageResizer from '@/utils/clientImageResizer';
import styles from './styles/modals.module.css';
import { COURSE_FIELD_RULES } from '@/config/courseFieldRules';

const { name: NAME_RULES, description: DESCRIPTION_RULES } = COURSE_FIELD_RULES;

// Helper component for character counting
function CharCount({ value = '', min, max }) {
  const length = value.length;

  const hasMin = min !== undefined && min !== null && min !== '';
  const hasMax = max !== undefined && max !== null && max !== '';

  const minNum = hasMin ? Number(min) : null;
  const maxNum = hasMax ? Number(max) : null;

  // If below the minimum, always show "more characters needed"
  if (minNum !== null && Number.isFinite(minNum) && length < minNum) {
    const needed = minNum - length;
    return (
      <div className={styles.charCounter}>
        {needed} more characters needed
      </div>
    );
  }

  // Once the minimum is reached, show how many are left (Optional) up to max
  if (maxNum !== null && Number.isFinite(maxNum) && length <= maxNum) {
    const left = maxNum - length;
    return (
      <div className={styles.charCounter}>
        {left} characters left (Optional)
      </div>
    );
  }

  return null;
}

/**
 * onSave(
 *   name: string,
 *   description: string,
 *   bannerUrl: string | null,
 *   bannerFilePath: string | null,
 *   introVideoUrl: string | null
 * )
 */
export default function HeroSectionModal({
  isOpen,
  onClose,
  courseContent,
  onSave,
}) {
  const [name, setName] = useState(courseContent.name);
  const [description, setDescription] = useState(courseContent.description);
  const [filePath, setFilePath] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [resizing, setResizing] = useState(false);

  // Video upload state
  const [videoPath, setVideoPath] = useState(courseContent.introVideoUrl || '');
  const [video, setVideo] = useState(null);
  const [vidUploading, setVidUploading] = useState(false);

  // ref to track validity in form
  const ref = useRef(null);

  const close = () => {
    setFile(null);
    setFilePath(null);
    setUploading(false);
    setResizing(false);
    setVideo(null);
    // keep videoPath so tutors see their current intro video when reopening
    setVidUploading(false);
    onClose();
  };

  async function uploadImage(e) {
    try {
      if (!e.target.files || e.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const originalFile = e.target.files[0];

      // Set resizing state
      setResizing(true);
      setUploading(false);

      // Resize image first using clientImageResizer
      const resizedFile = await clientImageResizer.resizeIfNeeded(originalFile);

      // Update state with resized file
      setFile(resizedFile);
      setFilePath(`temp-${crypto.randomUUID()}`); // Temporary path for display

      console.log(
        `Image processed: ${originalFile.size} bytes -> ${resizedFile.size} bytes`
      );
    } catch (err) {
      alert('Error processing image.');
      console.error('Image processing error:', err);
    } finally {
      setResizing(false);
      if (e.target) e.target.value = '';
    }
  }

  const handleVideoChange = async (e) => {
    const vid = e.target.files?.[0];

    if (!vid) {
      setVideo(null);
      setVideoPath('');
      return;
    }

    // Max 25GB
    const maxSize = 25 * 1024 * 1024 * 1024;
    if (vid.size > maxSize) {
      alert('Video size must be less than 25GB');
      console.error('Video size must be less than 25GB');
      setVideo(null);
      setVideoPath('');
      return;
    }

    setVideo(vid);
    await uploadVideo(vid);
  };

  async function uploadVideo(vid) {
    if (!vid) {
      console.error('Please send a video');
      return;
    }

    setVidUploading(true);

    try {
      // 1) Get signed URL for upload
      const response = await fetch('/api/demanding-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: vid.name, bucket: 'course-vids' }),
      });

      let uploadMeta;
      try {
        uploadMeta = await response.json();
      } catch (jsonErr) {
        console.error(
          'Failed to parse JSON from /api/demanding-upload:',
          jsonErr
        );
        throw new Error(
          `Failed to get signed URL (invalid JSON, status ${response.status})`
        );
      }

      if (!response.ok) {
        console.error('Error from /api/demanding-upload:', uploadMeta);
        throw new Error(
          uploadMeta?.error ||
            `Failed to get signed URL (status ${response.status})`
        );
      }

      const { uploadUrl, fileName } = uploadMeta;
      if (!uploadUrl || !fileName) {
        console.error(
          'Missing uploadUrl or fileName from /api/demanding-upload:',
          uploadMeta
        );
        throw new Error(
          'Failed to get signed URL (missing uploadUrl or fileName)'
        );
      }

      // 2) Upload file to storage using signed URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: vid,
        headers: { 'Content-Type': vid.type || 'application/octet-stream' },
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text().catch(() => '');
        console.error(
          'Failed to upload video to storage:',
          uploadResponse.status,
          errorText
        );
        throw new Error(
          `Failed to upload video to storage (status ${uploadResponse.status})`
        );
      }

      // 3) Get a public/signed URL for the uploaded video
      const urlResponse = await fetch('/api/demanding-upload/get-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName, bucket: 'course-vids' }),
      });

      let urlData;
      try {
        urlData = await urlResponse.json();
      } catch (jsonErr) {
        console.error(
          'Failed to parse JSON from /api/demanding-upload/get-url:',
          jsonErr
        );
        throw new Error(
          `Failed to get video URL (invalid JSON, status ${urlResponse.status})`
        );
      }

      if (!urlResponse.ok) {
        console.error('Error from /api/demanding-upload/get-url:', urlData);
        throw new Error(
          urlData?.error ||
            `Failed to get video URL (status ${urlResponse.status})`
        );
      }

      const { imageUrl } = urlData;
      if (!imageUrl) {
        console.error(
          'Missing imageUrl from /api/demanding-upload/get-url:',
          urlData
        );
        throw new Error('Failed to get video URL (missing imageUrl)');
      }

      // This is the final URL you should save in the Course row (introVidUrl)
      setVideoPath(imageUrl);
    } catch (err) {
      alert(`Error uploading video: ${err.message}`);
      console.error('Video upload error:', err);
    } finally {
      setVidUploading(false);
    }
  }

  const handleSubmitHero = async (e) => {
    e.preventDefault();

    // check html form errors
    if (!ref.current.checkValidity()) {
      ref.current.reportValidity();
      return;
    }

    try {
      let url = null;
      let actualFilePath = null;

      if (file) {
        // Set uploading state during actual upload
        setUploading(true);

        // Upload image through API using service role key
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bucket', 'course-imgs');

        const uploadResponse = await fetch('/api/upload-image', {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          throw new Error(errorData.error || 'Image upload failed');
        }

        const uploadResult = await uploadResponse.json();
        url = uploadResult.imageUrl;
        actualFilePath = uploadResult.filePath;

        setUploading(false);
      }

      // 🔴 IMPORTANT: pass videoPath to onSave so parent can write introVidUrl
      onSave(name, description, url, actualFilePath, videoPath || null);
    } catch (err) {
      alert(`Error uploading image: ${err.message}`);
      console.error(err);
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal} onClick={close}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.close} onClick={close}>
          &times;
        </span>
        <h2>Edit Hero Section:</h2>

        <form ref={ref} onSubmit={handleSubmitHero}>
          <label className={styles.formLabel} htmlFor="name">
            Course Title
          </label>
          <p className={styles.helperText}>
            Write a catchy, descriptive title. Avoid emojis.
          </p>
          <input
            type="text"
            id="name"
            className={styles.formInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required={NAME_RULES.required}
            minLength={NAME_RULES.minLength}
            maxLength={NAME_RULES.maxLength}
          />
          <CharCount
            value={name || ''}
            min={NAME_RULES.minLength}
            max={NAME_RULES.maxLength}
          />

          <label className={styles.formLabel} htmlFor="description">
            Course Description
          </label>
          <p className={styles.helperText}>
            Describe your class and what makes it unique in 1–3 sentences. Use
            clear keywords so parents can find it easily, and avoid emojis.
          </p>
          <textarea
            id="description"
            className={styles.courseDescriptionInput}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ whiteSpace: 'pre-wrap' }}
            required={DESCRIPTION_RULES.required}
            minLength={DESCRIPTION_RULES.minLength}
            maxLength={DESCRIPTION_RULES.maxLength}
          />
          <CharCount
            value={description || ''}
            min={DESCRIPTION_RULES.minLength}
            max={DESCRIPTION_RULES.maxLength}
          />

          <label htmlFor="imageUpload" className={styles.formLabel}>
            New Banner Photo (optional)
          </label>
          <p className={styles.helperText}>
            Recommended: 500×400px or larger
            <br />
            Formats: JPEG, PNG, WebP
            <br />
            Max size: 10MB input (optimized ≤5MB)
            <br />
            Your image will be automatically optimized for different display
            sizes.
          </p>
          <div className={styles.formInput}>
            <label
              htmlFor="imageUpload"
              className={`${styles.btn} ${styles.btnPrimary}`}
            >
              {resizing
                ? 'Resizing...'
                : uploading
                ? 'Uploading...'
                : 'Upload'}
            </label>
            {file && <label htmlFor="imageUpload">{filePath}</label>}
            <input
              id="imageUpload"
              type="file"
              accept="image/*"
              onChange={uploadImage}
              disabled={uploading || resizing}
              className={styles.uploadInput}
            />
          </div>

          <label htmlFor="introVideoUpload" className={styles.formLabel}>
            Course Introduction Video (optional)
          </label>
          <p className={styles.helperText}>
            Recommended: 1920×1080px or larger
            <br />
            Formats: MP4, MOV, MKV, AVI
            <br />
            Max size: 25GB input
            <br />
            Your video will be optimized for smooth playback across devices.
          </p>
          <div className={styles.formInput}>
            <label
              htmlFor="introVideoUpload"
              className={`${styles.btn} ${styles.btnPrimary}`}
            >
              {vidUploading ? 'Uploading...' : 'Upload Video'}
            </label>

            {video && !videoPath && <span>Selected: {video.name}</span>}

            <input
              id="introVideoUpload"
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              disabled={vidUploading}
              className={styles.uploadInput}
            />
          </div>

          {videoPath && (
            <div className={styles.videoContainer}>
              <video src={videoPath} className={styles.videoElement} controls />
            </div>
          )}

          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            type="submit"
            style={{ marginTop: '1rem' }}
          >
            Save
          </button>
        </form>
      </div>
    </div>
  );
}